import paper from 'paper'

export default class SpiceRack {
  constructor(name, data, grainDirection, region) {
    this.data = data
    this.grainDirection = grainDirection
    this.region = region

    // Dimensions
    this.materialThickness = 18
    this.baseShelfGap = 24
    this.smallShelfGap = 192
    this.largeShelfGap = 292
    this.dowelStartOffset = 25
    this.dowelSmallSpacing = 30
    this.dowelLargeSpacing = 50
    this.dowelDiameter = 5
    this.batonHeight = 40
    this.labelPadding = 40
    this.labelSize = 40

    // Dowel color mapping
    this.dowelColours = {
      oak: '#D4A574',
      walnut: '#5D4037',
      maple: '#F5DEB3',
      sapele: '#8B4513',
      ash: '#E8DCC4'
    }

    // Create components
    this.createBackground()
    this.createFrames()
    this.shelfPositions = this.calculateShelfPositions()
    this.createShelves()
    this.createBatons()
    this.createDowels()

    let label = this.getLabel()
    label.name = 'label'
    this.outline.name = 'outline'
    this.dimension = this.getDimension()
    this.dimension.name = 'dimension'

    let itemGroup = [this.background, this.outline, this.batons, this.shelves, this.dowels, label, this.dimension]
    this.item = new paper.Group([...itemGroup])
    this.item.name = name
    this.item.data.originalBounds = this.item.bounds
  }

  createBackground() {
    // Invisible rectangle covering the interior area for drag handling
    let background = new paper.Path.Rectangle(
      new paper.Point(this.materialThickness, 0),
      new paper.Size(this.data.w - this.materialThickness * 2, this.data.h)
    )
    background.fillColor = 'rgba(0,0,0,0.001)' // Nearly invisible but still captures mouse events
    background.strokeColor = null
    background.selectedColor = 'transparent'

    this.background = background
    this.background.name = 'background'
  }

  createFrames() {
    const frameColor = this.data.frame ? this.data.frame.uiColour : '#FBE6CB'

    // Left frame
    const leftFrame = new paper.Path.Rectangle(
      new paper.Point(0, 0),
      new paper.Size(this.materialThickness, this.data.h)
    )
    leftFrame.fillColor = frameColor
    leftFrame.strokeColor = 'black'

    // Right frame
    const rightFrame = new paper.Path.Rectangle(
      new paper.Point(this.data.w - this.materialThickness, 0),
      new paper.Size(this.materialThickness, this.data.h)
    )
    rightFrame.fillColor = frameColor
    rightFrame.strokeColor = 'black'

    this.outline = new paper.Group([leftFrame, rightFrame])
  }

  calculateShelfPositions() {
    // Calculate shelf positions from bottom up

    if (this.data.size === 'small') {
      // small (2 shelves) - calculate from bottom up

      // Bottom shelf: baton bottom at h - baseShelfGap, shelf top = baton bottom - batonHeight
      const shelfY1 = this.data.h - this.baseShelfGap - this.batonHeight

      // Top shelf: large gap above bottom shelf
      const shelfY0 = shelfY1 - this.largeShelfGap

      // Convert shelfY to yPos: yPos = shelfY - dowelStartOffset*2 - dowelSpacing - dowelDiameter
      const yPos0 = shelfY0 - this.dowelStartOffset * 2 - this.dowelSmallSpacing - this.dowelDiameter
      const yPos1 = shelfY1 - this.dowelStartOffset * 2 - this.dowelLargeSpacing - this.dowelDiameter

      return [
        { yPos: yPos0, dowelSpacing: this.dowelSmallSpacing },
        { yPos: yPos1, dowelSpacing: this.dowelLargeSpacing }
      ]
    } else {
      // large (4 shelves) - calculate from bottom up

      // Bottom shelf: baton bottom at h - baseShelfGap, shelf top = baton bottom - batonHeight
      const shelfY3 = this.data.h - this.baseShelfGap - this.batonHeight

      // Calculate shelf Y positions going up (gaps are shelf top to shelf top)
      const shelfY2 = shelfY3 - this.largeShelfGap
      const shelfY1 = shelfY2 - this.largeShelfGap
      const shelfY0 = shelfY1 - this.smallShelfGap

      // Convert shelfY to yPos: yPos = shelfY - dowelStartOffset*2 - dowelSpacing - dowelDiameter
      const yPos0 = shelfY0 - this.dowelStartOffset * 2 - this.dowelSmallSpacing - this.dowelDiameter
      const yPos1 = shelfY1 - this.dowelStartOffset * 2 - this.dowelSmallSpacing - this.dowelDiameter
      const yPos2 = shelfY2 - this.dowelStartOffset * 2 - this.dowelLargeSpacing - this.dowelDiameter
      const yPos3 = shelfY3 - this.dowelStartOffset * 2 - this.dowelLargeSpacing - this.dowelDiameter

      return [
        { yPos: yPos0, dowelSpacing: this.dowelSmallSpacing },
        { yPos: yPos1, dowelSpacing: this.dowelSmallSpacing },
        { yPos: yPos2, dowelSpacing: this.dowelLargeSpacing },
        { yPos: yPos3, dowelSpacing: this.dowelLargeSpacing }
      ]
    }
  }

  createShelves() {
    const shelves = []
    const shelfWidth = this.data.w - this.materialThickness * 2

    this.shelfPositions.forEach((shelfData) => {
      // Shelf positioned after both dowels
      const shelfY =
        shelfData.yPos + this.dowelStartOffset + shelfData.dowelSpacing + this.dowelDiameter + this.dowelStartOffset

      let shelf = new paper.Path.Rectangle(
        new paper.Point(this.materialThickness, shelfY),
        new paper.Size(shelfWidth, this.materialThickness)
      )

      shelf.fillColor = '#FBE6CB'
      shelf.strokeColor = 'black'
      shelf.selectedColor = 'transparent'
      shelves.push(shelf)
    })

    this.shelves = new paper.Group(shelves)
    this.shelves.name = 'shelves'
  }

  createBatons() {
    const batons = []
    const batonWidth = this.data.w - this.materialThickness * 2
    const frameColor = this.data.frame ? this.data.frame.uiColour : '#FBE6CB'

    this.shelfPositions.forEach((shelfData) => {
      const batonY =
        shelfData.yPos + this.dowelStartOffset + shelfData.dowelSpacing + this.dowelDiameter + this.dowelStartOffset

      let baton = new paper.Path.Rectangle(
        new paper.Point(this.materialThickness, batonY),
        new paper.Size(batonWidth, this.batonHeight)
      )

      baton.fillColor = frameColor
      baton.strokeColor = 'black'
      baton.selectedColor = 'transparent'
      batons.push(baton)
    })

    this.batons = new paper.Group(batons)
    this.batons.name = 'batons'
  }

  createDowels() {
    const dowels = []
    const dowelWidth = this.data.w - this.materialThickness * 2
    const dowelColor = this.dowelColours[this.data.dowel] || '#D4A574'

    this.shelfPositions.forEach((shelfData) => {
      // Two dowels above each shelf
      const firstDowelY = shelfData.yPos + this.dowelStartOffset
      const secondDowelY = shelfData.yPos + this.dowelStartOffset + shelfData.dowelSpacing

      // First dowel
      let dowel1 = new paper.Path.Rectangle(
        new paper.Point(this.materialThickness, firstDowelY),
        new paper.Size(dowelWidth, this.dowelDiameter)
      )
      dowel1.fillColor = dowelColor
      dowel1.strokeColor = 'black'
      dowel1.strokeWidth = 0.5
      dowel1.selectedColor = 'transparent'
      dowels.push(dowel1)

      // Second dowel (spaced according to large or small spacing)
      let dowel2 = new paper.Path.Rectangle(
        new paper.Point(this.materialThickness, secondDowelY),
        new paper.Size(dowelWidth, this.dowelDiameter)
      )
      dowel2.fillColor = dowelColor
      dowel2.strokeColor = 'black'
      dowel2.strokeWidth = 0.5
      dowel2.selectedColor = 'transparent'
      dowels.push(dowel2)
    })

    this.dowels = new paper.Group(dowels)
    this.dowels.name = 'dowels'
  }

  getLabel() {
    const text = new paper.PointText(new paper.Point(this.data.w / 2, this.data.h + this.labelSize + this.labelPadding))
    text.justification = 'center'
    text.fillColor = 'black'
    text.selectedColor = 'transparent'
    text.fontSize = this.labelSize
    text.fontFamily = 'Roboto Mono'
    text.content = this.data.name ? 'Spice Rack - ' + this.data.name : 'SPICE RACK'

    // Combined frame/dowel color dot with diagonal split
    const dotRadius = 20
    const cx = (this.data.w - text.bounds.width) / 2 - this.labelPadding
    const cy = this.data.h + text.bounds.height / 2 + this.labelPadding

    const frameColor = this.data.frame ? this.data.frame.uiColour : '#FBE6CB'
    const dowelColor = this.dowelColours[this.data.dowel] || '#D4A574'

    // Frame half (top-left) - 135 to 315 degrees
    let frameHalf = new paper.Path()
    frameHalf.add(new paper.Point(cx, cy))
    for (let angle = 135; angle <= 315; angle += 5) {
      const rad = (angle * Math.PI) / 180
      frameHalf.add(new paper.Point(cx + dotRadius * Math.cos(rad), cy + dotRadius * Math.sin(rad)))
    }
    frameHalf.closePath()
    frameHalf.fillColor = frameColor
    frameHalf.selectedColor = 'transparent'

    // Dowel half (bottom-right) - 315 to 495 (135) degrees
    let dowelHalf = new paper.Path()
    dowelHalf.add(new paper.Point(cx, cy))
    for (let angle = 315; angle <= 495; angle += 5) {
      const rad = (angle * Math.PI) / 180
      dowelHalf.add(new paper.Point(cx + dotRadius * Math.cos(rad), cy + dotRadius * Math.sin(rad)))
    }
    dowelHalf.closePath()
    dowelHalf.fillColor = dowelColor
    dowelHalf.selectedColor = 'transparent'

    // Outer stroke circle
    let dotOutline = new paper.Path.Circle(new paper.Point(cx, cy), dotRadius)
    dotOutline.strokeColor = 'black'
    dotOutline.fillColor = null
    dotOutline.selectedColor = 'transparent'

    // Diagonal line (top-right to bottom-left)
    let diagonalLine = new paper.Path.Line(
      new paper.Point(cx + dotRadius * 0.707, cy - dotRadius * 0.707),
      new paper.Point(cx - dotRadius * 0.707, cy + dotRadius * 0.707)
    )
    diagonalLine.strokeColor = 'black'
    diagonalLine.selectedColor = 'transparent'

    return new paper.Group([text, frameHalf, dowelHalf, dotOutline, diagonalLine])
  }

  getDimension() {
    let width, height, depth
    this.region == 'us' ? (width = this.data.w / 25.4) : (width = this.data.w)
    this.region == 'us' ? (height = this.data.h / 25.4) : (height = this.data.h)
    this.region == 'us' ? (depth = this.data.d / 25.4) : (depth = this.data.d)

    var dimension = new paper.PointText(new paper.Point(-this.labelPadding, this.data.h / 2))
    dimension.justification = 'center'
    dimension.fillColor = 'transparent'
    dimension.selectedColor = 'transparent'
    dimension.fontSize = this.labelSize
    dimension.fontFamily = 'Roboto Mono'
    dimension.content = `W${parseFloat(width.toFixed(3))} x H${parseFloat(height.toFixed(3))} x D${parseFloat(
      depth.toFixed(3)
    )}`
    dimension.rotate(90)
    return dimension
  }
}
